import type { Referral } from '../services/apiService';

export type ReferralBlocker = {
  code: string;
  field?: string;
  label: string;
  problem: string;
  action: string;
  tone: 'error' | 'warning';
};

const FIELD_LABELS: Record<string, string> = {
  patient_first_name: 'First Name',
  patient_last_name: 'Last Name',
  patient_dob: 'Date of Birth',
  nhi_number: 'NHI Number',
  contact_method: 'Contact Method (phone or email)',
  contact_phone: 'Contact Phone',
  contact_email: 'Contact Email',
  clinical_notes: 'Clinical Notes',
  referral_type: 'Referral Type',
  body_side: 'Body Side',
  body_part: 'Body Part',
  acc_claim_number: 'ACC Claim Number',
  acc_number_validation: 'ACC Claim Validation',
  referring_provider_name: 'Provider Name',
  referring_provider_org: 'Provider Organisation',
  referring_provider_contact: 'Provider Contact',
  referring_provider_email: 'Provider Email',
  referring_provider_edi: 'Provider EDI',
};

const ACC_OUTCOMES: Record<string, { label: string; problem: string; action: string }> = {
  INVALID_FORMAT: {
    label: 'ACC Claim Number',
    problem: 'The ACC claim number format is invalid.',
    action: 'Correct the claim number and re-validate it.',
  },
  NOT_FOUND: {
    label: 'ACC Claim Validation',
    problem: 'The ACC registry could not find this claim.',
    action: 'Check the claim number, then re-validate or contact ACC.',
  },
  VALID_PENDING: {
    label: 'ACC Claim Validation',
    problem: 'The ACC claim is still pending validation.',
    action: 'Wait for ACC to confirm the claim, then re-validate.',
  },
  VALIDATION_UNAVAILABLE: {
    label: 'ACC Claim Validation',
    problem: 'The ACC registry lookup was unavailable.',
    action: 'Retry validation later; contact support if it keeps failing.',
  },
  DECLINED: {
    label: 'ACC Claim Declined',
    problem: 'ACC declined this claim.',
    action: 'A staff member must approve the declined claim with a note before continuing.',
  },
};

export function getFieldDisplayLabel(fieldName: string): string {
  return FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function parseMissingFields(value: Referral['missing_fields']): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function getReferralBlockers(referral: Referral): ReferralBlocker[] {
  const blockers: ReferralBlocker[] = [];
  const missingFields = parseMissingFields(referral.missing_fields);
  const accStatus = String(referral.acc_validation_status_current || '').toUpperCase();
  const declinedAccApproved = Boolean(referral.declined_acc_approved_at);

  for (const field of missingFields) {
    if (field === 'acc_number_validation' && (declinedAccApproved || ACC_OUTCOMES[accStatus])) continue;
    const label = getFieldDisplayLabel(field);
    blockers.push({
      code: `MISSING_${field.toUpperCase()}`,
      field,
      label,
      problem: `${label} is missing or has not passed validation.`,
      action: `Edit ${label} and save the referral before retrying.`,
      tone: 'error',
    });
  }

  if (!declinedAccApproved && ACC_OUTCOMES[accStatus] && accStatus !== 'VALID' && accStatus !== 'INTERIM_ACCEPT' && accStatus !== 'ACCREDITED_EMPLOYER') {
    const outcome = ACC_OUTCOMES[accStatus];
    blockers.push({
      code: `ACC_${accStatus}`,
      field: 'acc_number_validation',
      ...outcome,
      tone: accStatus === 'DECLINED' ? 'warning' : 'error',
    });
  } else if (!declinedAccApproved && referral.funder?.toUpperCase().startsWith('ACC') && referral.is_complete !== true && missingFields.includes('acc_number_validation')) {
    blockers.push({
      code: 'ACC_NOT_VALIDATED',
      field: 'acc_number_validation',
      label: 'ACC Claim Validation',
      problem: 'The ACC claim has not been successfully validated.',
      action: 'Run ACC validation and resolve any result before retrying.',
      tone: 'error',
    });
  }

  if (['osteoarthritis_clinic', 'reds'].includes(String(referral.referral_type))) {
    blockers.push({
      code: 'STALE_REFERRAL_TYPE',
      field: 'referral_type',
      label: 'Referral Type',
      problem: 'The selected Referral Type is no longer supported.',
      action: 'Choose a current Referral Type and save the referral.',
      tone: 'error',
    });
  }

  const canComplete = referral.status === 'ready_for_indici' ||
    (referral.status === 'awaiting_reply' && referral.is_complete === true) ||
    (referral.is_miscellaneous === true && referral.miscellaneous_reason?.toLowerCase() === 'icp' && referral.is_complete === true);
  if (!['completed', 'cancelled'].includes(String(referral.status)) && referral.is_complete === true && !canComplete) {
    blockers.push({
      code: 'STATUS_GATE',
      label: 'Referral Status',
      problem: `The referral is complete but is still in ${getStatusLabel(referral.status)}.`,
      action: 'Move it to Ready for Indici through the normal workflow before completing it.',
      tone: 'warning',
    });
  }

  return blockers;
}

function getStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
